import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserCheck, 
  UserX, 
  ArrowRightLeft, 
  Search, 
  Filter, 
  RefreshCw, 
  Building2, 
  CheckCircle, 
  AlertTriangle, 
  HelpCircle, 
  History, 
  Edit3, 
  ChevronLeft, 
  ChevronRight,
  School,
  Calendar,
  Layers,
  FileSpreadsheet,
  Info,
  Clock
} from 'lucide-react';

interface StudentRecord {
  id: number;
  srn: string;
  student_name: string;
  father_name: string;
  mother_name: string;
  gender: string;
  roll_number: string;
  section: string;
  class_id: number;
  class_name: string;
  school_id: number;
  school_name: string;
  school_code: string;
  block: string;
  academic_session_id: number;
  academic_year: string;
  enrollment_status: string;
  raw_status: string;
  status_effective_date: string | null;
  status_reason: string | null;
  destination_school_name: string | null;
  status_remarks: string | null;
  status_updated_at: string | null;
  marks_count: number;
}

interface ReconciliationBlock {
  block: string;
  schools_count: number;
  enrolled: number;
  active: number;
  transferred: number;
  left_school: number;
  inactive: number;
  unverified: number;
  boys_active: number;
  girls_active: number;
  class_10_active: number;
  class_12_active: number;
}

interface ReconciliationSchool {
  school_id: number;
  school_name: string;
  school_code: string;
  block: string;
  enrolled: number;
  active: number;
  transferred: number;
  left_school: number;
  inactive: number;
  unverified: number;
  boys_active: number;
  girls_active: number;
  class_10_active: number;
  class_12_active: number;
}

interface ReconciliationData {
  academic_session_id: number;
  district_total: {
    enrolled: number;
    active: number;
    transferred: number;
    left_school: number;
    inactive: number;
    unverified: number;
    boys_active: number;
    girls_active: number;
    class_10_active: number;
    class_12_active: number;
  };
  block_summary: ReconciliationBlock[];
  school_summary: ReconciliationSchool[];
}

export const StudentLifecycleManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'reconciliation' | 'schools' | 'directory'>('reconciliation');
  const [loading, setLoading] = useState(true);
  const [reconciliation, setReconciliation] = useState<ReconciliationData | null>(null);
  const [students, setStudents] = useState<StudentRecord[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [schoolsList, setSchoolsList] = useState<{ id: number; school_name: string; school_code: string; block: string }[]>([]);

  // Directory Filters
  const [search, setSearch] = useState('');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');

  // Status Change Modal State
  const [selectedStudent, setSelectedStudent] = useState<StudentRecord | null>(null);
  const [newStatus, setNewStatus] = useState('ACTIVE');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusReason, setStatusReason] = useState('Administrative Verification');
  const [destSchoolId, setDestSchoolId] = useState<number | ''>('');
  const [destSchoolName, setDestSchoolName] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Status History Modal State
  const [viewHistoryStudent, setViewHistoryStudent] = useState<any | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchReconciliation();
    fetchSchoolsList();
  }, []);

  useEffect(() => {
    if (activeTab === 'directory') {
      fetchStudents(1);
    }
  }, [activeTab, selectedBlock, selectedSchool, selectedClass, selectedStatus, selectedGender]);

  const fetchReconciliation = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/students/reconciliation-matrix?academic_session_id=3', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setReconciliation(data.data);
      }
    } catch (err) {
      console.error('Error fetching reconciliation matrix:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolsList = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/schools', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSchoolsList(data.data || []);
      }
    } catch (err) {
      console.error('Error loading schools list:', err);
    }
  };

  const fetchStudents = async (pageToFetch = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(pageToFetch),
        limit: '20',
        academic_session_id: '3'
      });

      if (search) params.append('search', search);
      if (selectedBlock !== 'all') params.append('block', selectedBlock);
      if (selectedSchool !== 'all') params.append('school_id', selectedSchool);
      if (selectedClass !== 'all') params.append('class_id', selectedClass);
      if (selectedStatus !== 'all') params.append('enrollment_status', selectedStatus);
      if (selectedGender !== 'all') params.append('gender', selectedGender);

      const res = await fetch(`/api/students/directory?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setStudents(data.data.records);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching students directory:', err);
    } finally {
      setLoading(false);
    }
  };

  const openStatusModal = (st: StudentRecord) => {
    setSelectedStudent(st);
    setNewStatus(st.enrollment_status || 'ACTIVE');
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setStatusReason('Administrative Verification');
    setDestSchoolId('');
    setDestSchoolName('');
    setStatusRemarks('');
    setModalSuccess(null);
    setModalError(null);
  };

  const handleStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;

    try {
      setSubmittingStatus(true);
      setModalError(null);
      setModalSuccess(null);
      const token = localStorage.getItem('token');

      const payload = {
        new_status: newStatus,
        effective_date: effectiveDate,
        reason: statusReason,
        destination_school_id: destSchoolId ? Number(destSchoolId) : null,
        destination_school_name: destSchoolName,
        remarks: statusRemarks
      };

      const res = await fetch(`/api/students/${selectedStudent.id}/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setModalSuccess(data.message);
        // Refresh local student record
        setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, enrollment_status: newStatus, status_reason: statusReason } : s));
        fetchReconciliation();
        setTimeout(() => {
          setSelectedStudent(null);
        }, 1200);
      } else {
        setModalError(data.message || 'Failed to update status.');
      }
    } catch (err: any) {
      setModalError(err.message || 'Network error occurred.');
    } finally {
      setSubmittingStatus(false);
    }
  };

  const openHistoryModal = async (studentId: number) => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/students/${studentId}/status-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setViewHistoryStudent(data.data);
      }
    } catch (err) {
      console.error('Error fetching student history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const s = (status || 'ACTIVE').toUpperCase();
    switch (s) {
      case 'ACTIVE':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300"><CheckCircle className="w-3 h-3 mr-1" /> Active</span>;
      case 'TRANSFERRED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-300"><ArrowRightLeft className="w-3 h-3 mr-1" /> Transferred</span>;
      case 'LEFT_SCHOOL':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-300"><UserX className="w-3 h-3 mr-1" /> Left School</span>;
      case 'DROPPED_OUT':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300"><AlertTriangle className="w-3 h-3 mr-1" /> Dropped Out</span>;
      case 'PASSED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300"><CheckCircle className="w-3 h-3 mr-1" /> Passed</span>;
      case 'UNVERIFIED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-300"><HelpCircle className="w-3 h-3 mr-1" /> Unverified</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-300">{s}</span>;
    }
  };

  const totals = reconciliation?.district_total || {
    enrolled: 16113,
    active: 16113,
    transferred: 0,
    left_school: 0,
    inactive: 0,
    unverified: 0,
    boys_active: 0,
    girls_active: 0,
    class_10_active: 0,
    class_12_active: 0
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-900 text-white rounded-2xl p-6 shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/30 text-blue-200 border border-blue-400/30">
                Department of School Education, Haryana
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Session 2026-27 (Active)
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-400" />
              Student Lifecycle & Enrolment Position
            </h1>
            <p className="text-slate-300 text-sm mt-1 max-w-2xl">
              Accurately distinguishes between Total Historical Enrolment and Current Active Students. Complete audit tracking for transfers, exits, and verification.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchReconciliation(); if (activeTab === 'directory') fetchStudents(pagination.page); }}
              className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-xl border border-white/20 transition-all flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards — Enrolled vs Active Distinction */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Total Enrolled */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Total Enrolled</span>
            <Building2 className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-black text-slate-800">{totals.enrolled.toLocaleString()}</div>
          <div className="text-xs text-slate-400 mt-1">Historical Master Records</div>
        </div>

        {/* Current Active */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 shadow-sm border border-emerald-200">
          <div className="flex items-center justify-between text-emerald-700 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Current Active</span>
            <UserCheck className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-emerald-800">{totals.active.toLocaleString()}</div>
          <div className="text-xs text-emerald-600 font-medium mt-1">Primary Operational KPI</div>
        </div>

        {/* Transferred */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Transferred</span>
            <ArrowRightLeft className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="text-2xl font-black text-indigo-700">{totals.transferred.toLocaleString()}</div>
          <div className="text-xs text-slate-400 mt-1">Moved to Other Schools</div>
        </div>

        {/* Left School */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Left School</span>
            <UserX className="w-4 h-4 text-rose-500" />
          </div>
          <div className="text-2xl font-black text-rose-700">{totals.left_school.toLocaleString()}</div>
          <div className="text-xs text-slate-400 mt-1">Exited / Relocated</div>
        </div>

        {/* Other Inactive */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Other Inactive</span>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-2xl font-black text-amber-700">{totals.inactive.toLocaleString()}</div>
          <div className="text-xs text-slate-400 mt-1">Passed / Dropped Out</div>
        </div>

        {/* Unverified */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Unverified</span>
            <HelpCircle className="w-4 h-4 text-purple-500" />
          </div>
          <div className="text-2xl font-black text-purple-700">{totals.unverified.toLocaleString()}</div>
          <div className="text-xs text-slate-400 mt-1">Verification Required</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center border-b border-slate-200 bg-white px-4 pt-2 rounded-t-xl">
        <button
          onClick={() => setActiveTab('reconciliation')}
          className={`px-5 py-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'reconciliation'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Layers className="w-4 h-4" /> Block Reconciliation Matrix
        </button>
        <button
          onClick={() => setActiveTab('schools')}
          className={`px-5 py-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'schools'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <School className="w-4 h-4" /> School-wise Breakdown (161 Schools)
        </button>
        <button
          onClick={() => setActiveTab('directory')}
          className={`px-5 py-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'directory'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Search className="w-4 h-4" /> Student Directory & Status Management
        </button>
      </div>

      {/* TAB 1: BLOCK RECONCILIATION MATRIX */}
      {activeTab === 'reconciliation' && (
        <div className="bg-white rounded-b-xl shadow-sm border border-slate-200 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">District & Block Enrolment Reconciliation</h3>
              <p className="text-xs text-slate-500">Official mathematical verification of Enrolled vs Active student counts across all 5 educational blocks.</p>
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg border border-blue-200">
              <Info className="w-4 h-4" /> Sum of Blocks = District Total (100% Reconciled)
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-700 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <th className="py-3.5 px-4">Block Name</th>
                  <th className="py-3.5 px-4 text-center">Schools</th>
                  <th className="py-3.5 px-4 text-right">Total Enrolled</th>
                  <th className="py-3.5 px-4 text-right text-emerald-700 bg-emerald-50/50">Current Active</th>
                  <th className="py-3.5 px-4 text-right text-indigo-700">Transferred</th>
                  <th className="py-3.5 px-4 text-right text-rose-700">Left School</th>
                  <th className="py-3.5 px-4 text-right">Other Inactive</th>
                  <th className="py-3.5 px-4 text-right">Unverified</th>
                  <th className="py-3.5 px-4 text-center">Active Ratio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                {reconciliation?.block_summary.map((b) => {
                  const ratio = b.enrolled > 0 ? ((b.active / b.enrolled) * 100).toFixed(1) : '100.0';
                  return (
                    <tr key={b.block} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-slate-900">{b.block}</td>
                      <td className="py-3 px-4 text-center text-slate-500">{b.schools_count}</td>
                      <td className="py-3 px-4 text-right font-semibold">{(b.enrolled || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-black text-emerald-700 bg-emerald-50/30">{(b.active || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-indigo-700">{(b.transferred || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-rose-700">{(b.left_school || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-slate-500">{(b.inactive || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-purple-700">{(b.unverified || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-700">
                          {ratio}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900 text-white font-black text-sm border-t-2 border-slate-900">
                  <td className="py-3.5 px-4">DISTRICT TOTAL (NUH)</td>
                  <td className="py-3.5 px-4 text-center text-slate-300">161</td>
                  <td className="py-3.5 px-4 text-right">{totals.enrolled.toLocaleString()}</td>
                  <td className="py-3.5 px-4 text-right text-emerald-400 bg-slate-800">{totals.active.toLocaleString()}</td>
                  <td className="py-3.5 px-4 text-right text-indigo-300">{totals.transferred.toLocaleString()}</td>
                  <td className="py-3.5 px-4 text-right text-rose-300">{totals.left_school.toLocaleString()}</td>
                  <td className="py-3.5 px-4 text-right text-slate-400">{totals.inactive.toLocaleString()}</td>
                  <td className="py-3.5 px-4 text-right text-purple-300">{totals.unverified.toLocaleString()}</td>
                  <td className="py-3.5 px-4 text-center text-emerald-400">
                    {totals.enrolled > 0 ? ((totals.active / totals.enrolled) * 100).toFixed(1) : '100.0'}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: SCHOOL-WISE ENROLMENT BREAKDOWN */}
      {activeTab === 'schools' && (
        <div className="bg-white rounded-b-xl shadow-sm border border-slate-200 p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">School-wise Student Position (161 Schools)</h3>
              <p className="text-xs text-slate-500">Breakdown of Enrolled vs Active students per institution.</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedBlock}
                onChange={(e) => setSelectedBlock(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white"
              >
                <option value="all">All Blocks (5)</option>
                <option value="Nuh">Nuh</option>
                <option value="Nagina">Nagina</option>
                <option value="Taoru">Taoru</option>
                <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
                <option value="Punhana">Punhana</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-[600px]">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="sticky top-0 bg-slate-100 z-10 text-slate-700 font-bold uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="py-3 px-3">School Code</th>
                  <th className="py-3 px-3">School Name</th>
                  <th className="py-3 px-3">Block</th>
                  <th className="py-3 px-3 text-right">Total Enrolled</th>
                  <th className="py-3 px-3 text-right text-emerald-700 bg-emerald-50">Active Students</th>
                  <th className="py-3 px-3 text-right">Boys (Active)</th>
                  <th className="py-3 px-3 text-right">Girls (Active)</th>
                  <th className="py-3 px-3 text-right">Class 10</th>
                  <th className="py-3 px-3 text-right">Class 12</th>
                  <th className="py-3 px-3 text-right text-indigo-700">Transferred</th>
                  <th className="py-3 px-3 text-right text-rose-700">Left School</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                {reconciliation?.school_summary
                  .filter(s => selectedBlock === 'all' || s.block === selectedBlock)
                  .map(s => (
                    <tr key={s.school_id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-2.5 px-3 font-mono font-bold text-blue-700">{s.school_code}</td>
                      <td className="py-2.5 px-3 font-semibold text-slate-900 max-w-xs truncate" title={s.school_name}>
                        {s.school_name}
                      </td>
                      <td className="py-2.5 px-3">{s.block}</td>
                      <td className="py-2.5 px-3 text-right font-bold">{s.enrolled}</td>
                      <td className="py-2.5 px-3 text-right font-black text-emerald-700 bg-emerald-50/40">{s.active}</td>
                      <td className="py-2.5 px-3 text-right text-slate-500">{s.boys_active}</td>
                      <td className="py-2.5 px-3 text-right text-slate-500">{s.girls_active}</td>
                      <td className="py-2.5 px-3 text-right text-slate-500">{s.class_10_active}</td>
                      <td className="py-2.5 px-3 text-right text-slate-500">{s.class_12_active}</td>
                      <td className="py-2.5 px-3 text-right text-indigo-700">{s.transferred}</td>
                      <td className="py-2.5 px-3 text-right text-rose-700">{s.left_school}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: STUDENT DIRECTORY & STATUS MANAGEMENT */}
      {activeTab === 'directory' && (
        <div className="bg-white rounded-b-xl shadow-sm border border-slate-200 p-6 space-y-6">
          {/* Search & Filter Bar */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Search input */}
              <div className="md:col-span-2 relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by Student Name, SRN, Roll No, School..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchStudents(1)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status filter */}
              <div>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full py-2 px-3 border border-slate-200 rounded-lg text-sm bg-white font-medium text-slate-700"
                >
                  <option value="all">All Statuses</option>
                  <option value="ACTIVE">ACTIVE (Currently Attending)</option>
                  <option value="TRANSFERRED">TRANSFERRED</option>
                  <option value="LEFT_SCHOOL">LEFT SCHOOL</option>
                  <option value="DROPPED_OUT">DROPPED OUT</option>
                  <option value="PASSED">PASSED / COMPLETED</option>
                  <option value="UNVERIFIED">UNVERIFIED</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>

              {/* Block Filter */}
              <div>
                <select
                  value={selectedBlock}
                  onChange={(e) => { setSelectedBlock(e.target.value); setSelectedSchool('all'); }}
                  className="w-full py-2 px-3 border border-slate-200 rounded-lg text-sm bg-white font-medium text-slate-700"
                >
                  <option value="all">All Blocks</option>
                  <option value="Nuh">Nuh</option>
                  <option value="Nagina">Nagina</option>
                  <option value="Taoru">Taoru</option>
                  <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
                  <option value="Punhana">Punhana</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2 border-t border-slate-200/60">
              {/* Class Filter */}
              <div>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full py-1.5 px-3 border border-slate-200 rounded-lg text-xs bg-white font-medium text-slate-700"
                >
                  <option value="all">All Classes</option>
                  <option value="1">Class 10</option>
                  <option value="2">Class 12</option>
                </select>
              </div>

              {/* School Filter */}
              <div className="md:col-span-2">
                <select
                  value={selectedSchool}
                  onChange={(e) => setSelectedSchool(e.target.value)}
                  className="w-full py-1.5 px-3 border border-slate-200 rounded-lg text-xs bg-white font-medium text-slate-700 truncate"
                >
                  <option value="all">All Schools (161)</option>
                  {schoolsList
                    .filter(s => selectedBlock === 'all' || s.block === selectedBlock)
                    .map(s => (
                      <option key={s.id} value={s.id}>
                        {s.school_code} — {s.school_name} ({s.block})
                      </option>
                    ))}
                </select>
              </div>

              {/* Search Actions */}
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setSearch('');
                    setSelectedBlock('all');
                    setSelectedSchool('all');
                    setSelectedClass('all');
                    setSelectedStatus('all');
                    setSelectedGender('all');
                  }}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg bg-white"
                >
                  Reset
                </button>
                <button
                  onClick={() => fetchStudents(1)}
                  className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm"
                >
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Student Table */}
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="py-3 px-3">SRN</th>
                  <th className="py-3 px-3">Student Name</th>
                  <th className="py-3 px-3">School Name</th>
                  <th className="py-3 px-3">Block</th>
                  <th className="py-3 px-3 text-center">Class / Sec</th>
                  <th className="py-3 px-3 text-center">Gender</th>
                  <th className="py-3 px-3 text-center">Status</th>
                  <th className="py-3 px-3 text-center">Marks Recorded</th>
                  <th className="py-3 px-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                {students.map((st) => (
                  <tr key={st.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-2.5 px-3 font-mono font-bold text-blue-700">{st.srn}</td>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-slate-900">{st.student_name}</div>
                      <div className="text-[10px] text-slate-400">Roll: {st.roll_number || 'N/A'} {st.father_name ? `• F: ${st.father_name}` : ''}</div>
                    </td>
                    <td className="py-2.5 px-3 max-w-xs truncate" title={st.school_name}>
                      <span className="font-semibold text-slate-800">{st.school_name}</span>
                    </td>
                    <td className="py-2.5 px-3">{st.block}</td>
                    <td className="py-2.5 px-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-800 font-bold">
                        {st.class_name} {st.section ? `(${st.section})` : ''}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">{st.gender}</td>
                    <td className="py-2.5 px-3 text-center">
                      {getStatusBadge(st.enrollment_status)}
                    </td>
                    <td className="py-2.5 px-3 text-center font-bold text-slate-700">
                      {st.marks_count} marks
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => openStatusModal(st)}
                          className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded font-bold text-[11px] border border-blue-200 transition-colors flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" /> Status
                        </button>
                        <button
                          onClick={() => openHistoryModal(st.id)}
                          className="p-1 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100"
                          title="View History & Transfers"
                        >
                          <History className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {students.length === 0 && !loading && (
                  <tr>
                    <td colSpan={9} className="text-center py-10 text-slate-400 font-medium">
                      No student records found matching the specified filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-slate-500 font-medium">
              Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.total, pagination.page * pagination.limit)} of {pagination.total.toLocaleString()} students
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchStudents(pagination.page - 1)}
                className="p-2 border border-slate-200 rounded-lg text-slate-600 disabled:opacity-40 hover:bg-slate-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-bold text-slate-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchStudents(pagination.page + 1)}
                className="p-2 border border-slate-200 rounded-lg text-slate-600 disabled:opacity-40 hover:bg-slate-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STATUS CHANGE MODAL */}
      {selectedStudent && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-blue-400" />
                  Update Student Enrolment Status
                </h3>
                <p className="text-xs text-slate-300 mt-0.5">
                  Audited status modification • Historical records remain preserved
                </p>
              </div>
              <button
                onClick={() => setSelectedStudent(null)}
                className="text-slate-400 hover:text-white text-xl font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleStatusSubmit} className="p-6 space-y-4 text-sm">
              {/* Student Summary Info */}
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200">
                <div className="font-bold text-slate-900 text-base">{selectedStudent.student_name}</div>
                <div className="text-xs text-slate-500 mt-1 grid grid-cols-2 gap-2">
                  <div><strong>SRN:</strong> <span className="font-mono text-blue-700">{selectedStudent.srn}</span></div>
                  <div><strong>Class:</strong> {selectedStudent.class_name} ({selectedStudent.section})</div>
                  <div className="col-span-2 truncate"><strong>School:</strong> {selectedStudent.school_name}</div>
                  <div><strong>Current Status:</strong> {getStatusBadge(selectedStudent.enrollment_status)}</div>
                </div>
              </div>

              {/* Status selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  New Status <span className="text-rose-500">*</span>
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg font-semibold text-slate-800 bg-white"
                  required
                >
                  <option value="ACTIVE">ACTIVE (Currently Attending School)</option>
                  <option value="TRANSFERRED">TRANSFERRED (Moved to another school)</option>
                  <option value="LEFT_SCHOOL">LEFT SCHOOL (Discontinued / Withdrawn)</option>
                  <option value="DROPPED_OUT">DROPPED OUT (Long Absenteeism)</option>
                  <option value="PASSED">PASSED / COMPLETED</option>
                  <option value="MIGRATED">MIGRATED (Out of District / State)</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="UNVERIFIED">UNVERIFIED (Requires School Verification)</option>
                </select>
              </div>

              {/* Effective Date */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Effective Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white"
                  required
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Reason for Status Change <span className="text-rose-500">*</span>
                </label>
                <select
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white mb-2"
                >
                  <option value="Administrative Verification">Administrative Verification</option>
                  <option value="Family Relocation">Family Relocation</option>
                  <option value="Joined Private School">Joined Private School</option>
                  <option value="Transferred to Other Govt School">Transferred to Other Govt School</option>
                  <option value="Completed Course / Passed">Completed Course / Passed</option>
                  <option value="Continuous Absence / Scribe">Continuous Absence / Scribe</option>
                  <option value="Other">Other Reason (Specify below)</option>
                </select>
              </div>

              {/* Destination School (If transferred) */}
              {newStatus === 'TRANSFERRED' && (
                <div className="space-y-2 p-3 bg-indigo-50/50 rounded-xl border border-indigo-200">
                  <label className="block text-xs font-bold text-indigo-900 uppercase">
                    Destination School (Within Nuh or Out of District)
                  </label>
                  <select
                    value={destSchoolId}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : '';
                      setDestSchoolId(val);
                      if (val) {
                        const sch = schoolsList.find(s => s.id === val);
                        if (sch) setDestSchoolName(sch.school_name);
                      }
                    }}
                    className="w-full p-2 border border-indigo-200 rounded-lg text-xs bg-white text-slate-800"
                  >
                    <option value="">Select from Nuh Schools (161)...</option>
                    {schoolsList.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.school_code} — {s.school_name} ({s.block})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Or type external school/district name..."
                    value={destSchoolName}
                    onChange={(e) => setDestSchoolName(e.target.value)}
                    className="w-full p-2 border border-indigo-200 rounded-lg text-xs bg-white"
                  />
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Administrative Remarks / Reference
                </label>
                <textarea
                  rows={2}
                  placeholder="E.g., SLC / Transfer Certificate No. 441 issued on 12/08/2026..."
                  value={statusRemarks}
                  onChange={(e) => setStatusRemarks(e.target.value)}
                  className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white"
                />
              </div>

              {modalSuccess && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> {modalSuccess}
                </div>
              )}

              {modalError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {modalError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setSelectedStudent(null)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-xs font-bold hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingStatus}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-md disabled:opacity-50 flex items-center gap-1.5"
                >
                  <CheckCircle className="w-3.5 h-3.5" /> Save Status Change
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STUDENT HISTORY & AUDIT MODAL */}
      {viewHistoryStudent && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden border border-slate-200 max-h-[85vh] flex flex-col">
            <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black flex items-center gap-2">
                  <History className="w-5 h-5 text-indigo-400" />
                  Student Lifecycle & Transfer Timeline
                </h3>
                <p className="text-xs text-slate-300">
                  {viewHistoryStudent.student.student_name} • SRN: {viewHistoryStudent.student.srn}
                </p>
              </div>
              <button
                onClick={() => setViewHistoryStudent(null)}
                className="text-slate-400 hover:text-white text-xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 text-sm">
              {/* Current Position */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><strong>Current School:</strong> <p className="text-xs text-slate-700">{viewHistoryStudent.student.school_name}</p></div>
                <div><strong>Block:</strong> <p className="text-xs text-slate-700">{viewHistoryStudent.student.block}</p></div>
                <div><strong>Enrolment Status:</strong> {getStatusBadge(viewHistoryStudent.student.enrollment_status)}</div>
                <div><strong>Class:</strong> <p className="text-xs text-slate-700">{viewHistoryStudent.student.class_name}</p></div>
                <div><strong>Preserved Marks:</strong> <p className="text-xs text-slate-700">{viewHistoryStudent.marks_summary.total_marks_recorded} assessments</p></div>
                <div><strong>Session:</strong> <p className="text-xs text-slate-700">{viewHistoryStudent.student.academic_year}</p></div>
              </div>

              {/* Status Audit Timeline */}
              <div>
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-blue-600" /> Status Change Audit Trail
                </h4>
                {viewHistoryStudent.history.length === 0 ? (
                  <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-lg">No status modifications recorded. Student is currently on initial master status.</p>
                ) : (
                  <div className="space-y-3">
                    {viewHistoryStudent.history.map((h: any) => (
                      <div key={h.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-800">
                            Changed to <span className="text-blue-700 font-black">{h.new_status}</span> (was {h.old_status})
                          </span>
                          <span className="text-slate-400 text-[11px]">{h.created_at}</span>
                        </div>
                        <div className="text-slate-600"><strong>Reason:</strong> {h.reason || 'Not specified'}</div>
                        {h.destination_school_name && (
                          <div className="text-indigo-700"><strong>Destination:</strong> {h.destination_school_name}</div>
                        )}
                        {h.remarks && <div className="text-slate-500 italic">"{h.remarks}"</div>}
                        <div className="text-[10px] text-slate-400 mt-1">Updated by: {h.changed_by_name || 'System Administrator'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transfer Records */}
              {viewHistoryStudent.transfers.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <ArrowRightLeft className="w-4 h-4 text-indigo-600" /> Transfer Ledger
                  </h4>
                  <div className="space-y-2">
                    {viewHistoryStudent.transfers.map((t: any) => (
                      <div key={t.id} className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-200 text-xs">
                        <div className="font-bold text-indigo-950">
                          From {t.from_school_name} → {t.resolved_to_school_name || t.to_school_name || 'External'}
                        </div>
                        <div className="text-slate-500 text-[11px] mt-0.5">Transfer Date: {t.transfer_date} • Reason: {t.transfer_reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setViewHistoryStudent(null)}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800"
              >
                Close Timeline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLifecycleManagementPage;
